import { useMutation, useQuery } from 'react-query';
import {
  apiGet,
  apiGetById,
  apiGetList,
  apiPatch,
  apiPost,
  apiPut,
  apiDelete,
  apiUpload,
} from '../services/apiClient';

/**
 * الإعلانات — the announcement pop-up that appears on the public website.
 *
 * Every call goes through `services/apiClient`, which applies the Backend API
 * Contract in one place. Components must never call `axios` directly.
 *
 * SPEC: DPA-FE `docs/Announcements&visitors/02-backend-api-request.md` Part A.
 * DESIGN RATIONALE: `01-development-roadmap.md`.
 *
 * ROUTES
 *
 *   GET    /api/Announcement/{page}/{pageSize}   list        §A2
 *   GET    /api/Announcement/{id}                one         §A3
 *   POST   /api/Announcement                     create      §A4
 *   PUT    /api/Announcement/{id}                update      §A5
 *   PATCH  /api/Announcement/{id}                publish     §A6
 *   DELETE /api/Announcement/{id}                delete      §A7
 *   POST   /api/Announcement/{id}/PreviewToken   preview     §A8.1
 *   POST   /api/Announcement/UploadImage         banner      §A9
 *   POST   /api/Announcement/{id}/ResetDismissals            §A10
 *
 * ✅ DEPLOYED 2026-10-06. Measured against the live API the same day. The routes
 * above are exactly as specified; the differences the backend documented in
 * `FRONTEND-NOTES.md` and that are handled in this file are:
 *
 *   - `imageWidth` / `imageHeight` added to A1/A3/preview (pixel size).
 *   - `layout: ImageOnly` accepts `ctaUrl` with NO `ctaLabel` — the whole
 *     banner is the link.
 *   - `imageUrl` must be a URL that `UploadImage` returned, else
 *     `ANNOUNCEMENT_IMAGE_INVALID`. Unused uploads are swept after 24 h.
 *   - PATCH takes `isPublished` ONLY, and requires it — see the note below.
 *   - Every success is HTTP 200, including create.
 *   - `error` arrives as an Arabic sentence and `errorCode` is always set, so
 *     the English exact-match map that `ContactSettings` needed is NOT needed
 *     here. Show `error` directly.
 *   - Field limits are enforced server-side; the form mirrors them (see
 *     `actionPage/AnnouncementForm.jsx` LIMITS) so an operator is told in the
 *     field rather than after a round trip.
 *
 * ❌ NOT DEPLOYED: the optional telemetry endpoint
 * `POST /api/Announcement/{id}/Event` (§A11) answers 405. No client code calls
 * it; the website's pop-up had its calls removed.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * TWO THINGS ABOUT THIS ENTITY THAT ARE NOT OBVIOUS
 * ────────────────────────────────────────────────────────────────────────────
 *
 * 1. `version` IS NOT AN AUDIT FIELD. It is the mechanism by which a corrected
 *    announcement is shown again to visitors who already dismissed the wrong
 *    one. The browser keys its "already dismissed" memory on `{id}.{version}`,
 *    so the backend bumping `version` resets everyone. The backend bumps it on
 *    a CONTENT change only — title, body, image, alt, CTA, layout — and
 *    deliberately NOT on `priority`, the schedule or `isPublished`, because
 *    those do not change what a visitor reads.
 *
 *    Every write below therefore returns the new `version`, and the screens use
 *    it to tell the operator whether their edit will be re-shown. That is
 *    exactly what they need to know before saving.
 *
 * 2. `status` IS SERVER-COMPUTED AND READ-ONLY. Draft / Scheduled / Live /
 *    Expired. The CMS must NOT re-derive it from `isPublished` + the window
 *    against the browser's clock: the website decides what is live against the
 *    SERVER's clock, and two clocks disagreeing is how the CMS ends up showing
 *    «منشور» for something no visitor can see. Display what the API sends.
 */

const BASE = 'Announcement';

/* -------------------------------------------------------------------------- */
/*  Reads                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * The list screen. Pagination in the path, filters in the query string — and
 * the filters COMPOSE, so a search inside a status inside a sort is one
 * request, not three.
 *
 * `page` is 1-BASED on this API (see the long note in `apiClient.apiGetList`).
 *
 * `keepPreviousData` keeps the current rows on screen while the next page
 * loads, so paging does not blank the table and jump the scroll position.
 */
export const useFetchAnnouncements = ({
  page = 1,
  pageSize = 10,
  searchText = '',
  status = '',
  sortBy = 'updatedOn',
  sortDirection = 'desc',
} = {}) =>
  useQuery({
    queryKey: [
      'announcements',
      page,
      pageSize,
      searchText,
      status,
      sortBy,
      sortDirection,
    ],
    queryFn: () =>
      apiGetList(BASE, {
        page,
        pageSize,
        searchText,
        status,
        sortBy,
        sortDirection,
      }),
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });

/**
 * One announcement, for the edit form. Off by default so the create form does
 * not fire a request for an id it does not have.
 */
export const useFetchAnnouncementById = (id, { enabled = false } = {}) =>
  useQuery({
    queryKey: ['announcement', id],
    queryFn: () => apiGetById(BASE, id),
    enabled: enabled && !!id,
    refetchOnWindowFocus: false,
  });

/**
 * What the website would show RIGHT NOW — the same anonymous endpoint the
 * public site calls.
 *
 * Shown on the list screen as a one-line "currently live" strip. It is not
 * decoration: `priority` means two simultaneously-active announcements resolve
 * to one, and an operator who has just published a second one needs to see
 * which of them actually won without opening the website.
 */
export const useFetchActiveAnnouncement = () =>
  useQuery({
    queryKey: ['announcementActive'],
    queryFn: () => apiGet(`${BASE}/Active`),
    refetchOnWindowFocus: false,
  });

/* -------------------------------------------------------------------------- */
/*  Writes                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * The payload for create and update, normalised in ONE place so the two cannot
 * drift — §A5 says PUT takes the complete entity, exactly as POST does.
 *
 * Three rules are enforced here rather than left to the form:
 *
 *  - EMPTY STRING IS NOT A VALUE. Contract §6: a missing optional field is
 *    `null`, never `""`. An empty `ctaUrl` sent as `""` is the difference
 *    between "no button" and "a button to nowhere".
 *  - `frequencyDays` ONLY EXISTS FOR `EveryNDays`. Sending it alongside
 *    `OncePerDevice` invites the backend to store a number nothing reads, and
 *    the next person to open the record cannot tell which field is in charge.
 *  - DATES GO OUT AS ISO-8601 UTC WITH `Z`. The form collects local wall-clock
 *    time in an `<input type="datetime-local">`, which has no zone at all;
 *    `new Date(value).toISOString()` is what converts the operator's Dubai
 *    afternoon into the instant the server will compare against. Getting this
 *    wrong is a four-hour scheduling error, and it is silent.
 */
const toPayload = (values = {}) => {
  const text = (value) => {
    const trimmed = typeof value === 'string' ? value.trim() : '';
    return trimmed === '' ? null : trimmed;
  };

  const instant = (value) => {
    if (!value) return null;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
  };

  const layout = values.layout || 'TextOnly';
  const displayFrequency = values.displayFrequency || 'OncePerSession';

  return {
    title: text(values.title),
    body: text(values.body),
    imageUrl: text(values.imageUrl),
    imageAlt: text(values.imageAlt),
    ctaLabel: text(values.ctaLabel),
    ctaUrl: text(values.ctaUrl),
    ctaOpensInNewTab: values.ctaOpensInNewTab === true,
    layout,
    displayFrequency,
    frequencyDays:
      displayFrequency === 'EveryNDays'
        ? Number(values.frequencyDays) || 1
        : null,
    startsOn: instant(values.startsOn),
    // `null` is meaningful: open-ended, never expires.
    endsOn: instant(values.endsOn),
    priority: Number.isFinite(Number(values.priority))
      ? Number(values.priority)
      : 0,
    isPublished: values.isPublished === true,
  };
};

export { toPayload as toAnnouncementPayload };

/** POST. Answers with `{ id, version }` so the screen can navigate to it. */
export const useCreateAnnouncement = () =>
  useMutation((values) => apiPost(BASE, toPayload(values)));

/**
 * PUT. The COMPLETE entity every time — the backend validates the whole record,
 * so there is no partial update to make, and a PUT that omitted a field would
 * clear it.
 */
export const useUpdateAnnouncement = () =>
  useMutation(({ id, ...values }) => apiPut(`${BASE}/${id}`, toPayload(values)));

/**
 * PATCH — publish, unpublish, enable, disable. All four words in the
 * requirement are this one boolean, flipped from the list screen without
 * opening the editor.
 *
 * Only the changed field is sent; §5.5 of the contract says absent fields are
 * left untouched. `priority` rides the same route for the same reason.
 *
 * The response carries the RECOMPUTED `status`, which is why the list does not
 * have to guess: publishing a record whose window has not opened yet makes it
 * `Scheduled`, not `Live`.
 */
export const useSetAnnouncementPublished = () =>
  useMutation(({ id, isPublished }) =>
    apiPatch(`${BASE}/${id}`, { isPublished })
  );

/**
 * ⚠ THERE IS NO PRIORITY PATCH, DELIBERATELY. §A6 of the request document said
 * "`priority` may also be PATCHed the same way from the list screen", and an
 * earlier version of this file exported a hook that did.
 *
 * THE BACKEND SHIPPED IT NARROWER: "PATCH accepts only `isPublished`, and it is
 * required" (FRONTEND-NOTES.md, 2026-10-06). A priority PATCH now answers
 * `400 ANNOUNCEMENT_VALIDATION_FAILED`. The hook was removed rather than left
 * as a trap for the next person to wire a priority control to.
 *
 * To change a priority, load the record (A3) and PUT it back (A5) — which is
 * what the edit form does. Note that a priority change does NOT bump `version`,
 * so it will not re-show the announcement to anyone.
 */

/** DELETE. Specified as idempotent (§A7) — a second delete is still a success. */
export const useDeleteAnnouncement = () =>
  useMutation((id) => apiDelete(BASE, id));

/**
 * "Show again to everyone" — §A10. Increments `version` and changes nothing
 * else.
 *
 * WHY IT EXISTS. An update only bumps `version` when the CONTENT changed, which
 * is correct: nobody wants a priority tweak re-opening a dialog for every
 * visitor who already closed it. But that leaves no way to re-show a correct,
 * unchanged announcement — an exam-day reminder dismissed last week, say —
 * short of making a cosmetic edit to force a bump. This is that way.
 */
export const useResetAnnouncementDismissals = () =>
  useMutation((id) => apiPost(`${BASE}/${id}/ResetDismissals`, {}));

/**
 * Mint a short-lived preview token (§A8.1) — TTL <= 15 minutes, scoped to one
 * announcement, read-only.
 *
 * The CMS then opens the WEBSITE's own preview route with it, rather than
 * drawing its own imitation of the pop-up. The two applications share no
 * components, so a preview drawn here would drift from the real dialog within a
 * release or two, and the operator would be approving something a visitor never
 * sees. See `01-development-roadmap.md` §3.4.
 */
export const useCreatePreviewToken = () =>
  useMutation((id) => apiPost(`${BASE}/${id}/PreviewToken`, {}));

/**
 * Banner upload (§A9) — multipart, `Content-Type` deliberately unset so the
 * browser supplies the boundary.
 *
 * ANSWERS WITH THE URL, which matters more than it sounds: this API has no
 * general asset endpoint, and the two per-resource uploads that do exist
 * (`Degree/UploadFileToBlob`, `Alumni/UploadBlob`) document no response schema
 * at all, so a caller cannot know where the file landed
 * (`DPA-CONFORMANCE.md` item 19). This one was specified to return an absolute,
 * directly usable `url` — the form stores it straight onto `imageUrl`.
 */
export const useUploadAnnouncementImage = () =>
  useMutation((file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiUpload(`${BASE}/UploadImage`, formData);
  });
